import Container from "@/components/container";
import { PreFooter } from "@/components/preFooter";
import ProgressiveBlur from "@/components/progressive-blur";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";

const PRODUCT_SIGNUP_HREF = "/auth/signin";
const PRODUCT_SHOWCASE_HREF = "/p/showcase";

const ANCHOR_OFFSET = "scroll-mt-24";

export function ProductBadge({ children }: { children: ReactNode }) {
  return (
    <div className="inline-block w-fit rounded-[13px] border border-neutral-200 bg-white px-3 py-1.5 text-[13px] font-medium leading-[1.4em] text-neutral-600">
      {children}
    </div>
  );
}

export function ProductHeroActions({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "mt-8 flex flex-col items-center justify-center gap-2 md:flex-row",
        className,
      )}
    >
      <Button asChild size="sm">
        <Link href={PRODUCT_SIGNUP_HREF}>Handout your slides</Link>
      </Button>
      <Button asChild size="sm" variant="outline">
        <Link href={PRODUCT_SHOWCASE_HREF}>See a live handyout</Link>
      </Button>
    </div>
  );
}

export function ProductRow({
  id,
  inset = true,
  className,
  containerClassName,
  children,
}: {
  id?: string;
  inset?: boolean;
  className?: string;
  containerClassName?: string;
  children: ReactNode;
}) {
  return (
    <Container
      id={id}
      className={cn(id && ANCHOR_OFFSET, className)}
      containerClassName={containerClassName}
    >
      <div className="grid grid-cols-12">
        <div
          className={cn(
            "col-span-full lg:col-[2/-2]",
            inset && "px-4 lg:px-0",
          )}
        >
          {children}
        </div>
      </div>
    </Container>
  );
}

export function DottedPanel({ children }: { children: ReactNode }) {
  return (
    <div
      className="px-4 pt-16 pb-16 lg:px-8 lg:pt-24"
      style={{
        backgroundImage:
          "radial-gradient(circle, #E4E4E7 1px, transparent 1.5px)",
        backgroundSize: "16px 16px",
        backgroundColor: "#fff",
      }}
    >
      {children}
    </div>
  );
}

function Corners() {
  return (
    <>
      <span className="pointer-events-none absolute -top-[7px] -left-[7px] select-none font-mono text-[13px] leading-none text-neutral-300">
        +
      </span>
      <span className="pointer-events-none absolute -top-[7px] -right-[7px] select-none font-mono text-[13px] leading-none text-neutral-300">
        +
      </span>
      <span className="pointer-events-none absolute -bottom-[7px] -left-[7px] select-none font-mono text-[13px] leading-none text-neutral-300">
        +
      </span>
      <span className="pointer-events-none absolute -bottom-[7px] -right-[7px] select-none font-mono text-[13px] leading-none text-neutral-300">
        +
      </span>
    </>
  );
}

export function CellGrid({
  columns = 3,
  children,
}: {
  columns?: 2 | 3;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 border-t border-l border-dashed border-neutral-200 md:grid-cols-2",
        columns === 3 && "lg:grid-cols-3",
      )}
    >
      {children}
    </div>
  );
}

export function Cell({
  id,
  n,
  caption,
  children,
}: {
  id?: string;
  n: string;
  caption: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      id={id}
      className={cn(
        "relative flex min-h-[380px] flex-col overflow-hidden border-b border-r border-dashed border-neutral-200 bg-white px-5 pt-6 pb-6 sm:px-7",
        id && ANCHOR_OFFSET,
      )}
    >
      <Corners />
      <div className="font-mono text-[11px] text-neutral-500">[ {n} ]</div>
      <div className="flex min-w-0 flex-1 items-center justify-center py-6">
        {children}
      </div>
      <div className="max-w-[320px] text-[15px] leading-snug text-neutral-600">
        {caption}
      </div>
    </div>
  );
}

export function LargeCell({
  id,
  n,
  title,
  sub,
  children,
}: {
  id?: string;
  n: string;
  title: string;
  sub: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      id={id}
      className={cn(
        "relative flex min-h-[420px] flex-col overflow-hidden border-b border-r border-dashed border-neutral-200 bg-white px-5 pt-7 pb-8 sm:px-8",
        id && ANCHOR_OFFSET,
      )}
    >
      <Corners />
      <div className="font-mono text-[12px] text-neutral-400">[{n}]</div>
      <div className="flex min-w-0 flex-1 items-center justify-center py-6">
        {children}
      </div>
      <div>
        <h3 className="mb-1 text-[16px] font-medium leading-snug text-neutral-900">
          {title}
        </h3>
        <p className="text-[14px] leading-snug text-neutral-500">{sub}</p>
      </div>
    </div>
  );
}

export function QuoteBanner({ quote, name }: { quote: string; name: string }) {
  return (
    <figure
      className="relative col-span-full flex min-h-[200px] flex-col justify-center gap-6 overflow-hidden border-b border-r border-dashed border-neutral-200 bg-neutral-50 px-5 py-12 sm:px-8 lg:flex-row lg:items-end lg:justify-between"
      style={{
        backgroundImage:
          "repeating-linear-gradient(90deg, rgba(24,24,27,0.045) 0 1px, transparent 1px 8px)",
      }}
    >
      <Corners />
      <blockquote className="max-w-[640px] text-balance text-2xl font-medium leading-tight tracking-tight text-neutral-900 md:text-3xl">
        &ldquo;{quote}&rdquo;
      </blockquote>
      <figcaption className="shrink-0 text-sm text-neutral-500">
        {name}
      </figcaption>
    </figure>
  );
}

export function ExampleNote({ className }: { className?: string }) {
  return (
    <p className={cn("mt-4 text-xs text-neutral-400", className)}>
      Mockups show example data.
    </p>
  );
}

export type BandItem = { icon: LucideIcon; title: string; text: string };

const BAND_STRIPES: CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(125deg, transparent, transparent 6px, #E4E4E7 6px, #E4E4E7 7px)",
};

export function FeatureBand({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle: string;
  items: BandItem[];
}) {
  return (
    <Container containerClassName="py-16 lg:py-32">
      <div className="flex flex-col">
        <div className="grid grid-cols-12">
          <div className="col-span-full px-4 lg:col-[2/-2] lg:px-0">
            <div className="pt-12 lg:pt-0">
              <h2 className="mb-10 max-w-[40rem] text-balance text-3xl font-medium leading-tight text-neutral-900">
                <span>{title}</span> <br className="hidden md:block" />
                <span className="text-neutral-500">{subtitle}</span>
              </h2>
            </div>
          </div>
        </div>

        <div className="relative flex-col border-y border-neutral-200">
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 bottom-0 left-0 hidden w-full md:block"
            style={BAND_STRIPES}
          />
          <div className="relative z-20 mx-4 grid grid-cols-1 self-center border-x border-neutral-200 bg-white md:mx-26 md:grid-cols-3">
            {items.map(({ icon: Icon, title: itemTitle, text }, index) => (
              <div
                key={itemTitle}
                className={cn(
                  "px-6 py-6",
                  index < items.length - 1 &&
                    "border-b border-neutral-200 md:border-b-0 md:border-r",
                )}
              >
                <div className="mb-2 inline-flex items-center gap-2">
                  <Icon className="size-4 text-neutral-700" />
                  <h3 className="text-[15px] font-medium text-neutral-900">
                    {itemTitle}
                  </h3>
                </div>
                <p className="max-w-[24em] text-sm leading-relaxed text-neutral-500">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  );
}

export function ProductClosing() {
  return (
    <>
      <Container className="mb-32">
        <PreFooter />
      </Container>
      <ProgressiveBlur blur="1rem" ratio={2} />
    </>
  );
}
